import {
  Flex,
  FormControl,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import {
  TemplateRangeTier,
  UpdateTemplateRangeTierRequest,
  templateRangeTiersApi,
} from '@texas/api/endpoints/metadata/templateRangeTiersApi';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { SubmitButton } from '@texas/components/shared/form/SubmitButton';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { HookFormCounter } from '@texas/components/shared/form/HookFormCounter';
import { SharedDisclosureProps } from '@texas/components/shared/types';
import { request } from '@texas/utils/helpers/httpHelpers';
import { useApiResource } from '@texas/api/hooks/useApiResource';

export interface EditVdcTierProps extends SharedDisclosureProps {
  onUpdated: (tier: TemplateRangeTier) => void;
  tierId: number | null;
}

export function EditVdcTier({
  onUpdated,
  tierId,
  onClose,
  isOpen,
}: EditVdcTierProps) {
  const { t } = useTranslation();

  return (
    <Modal
      variant="entityDirectory"
      isOpen={isOpen}
      onClose={onClose}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>{t('configuration.editVdcTier')}</ModalHeader>
        {tierId && (
          <Form tierId={tierId} onClose={onClose} onUpdated={onUpdated} />
        )}
      </ModalContent>
    </Modal>
  );
}

function Form({
  tierId,
  onClose,
  onUpdated,
}: {
  tierId: number;
  onClose: () => void;
  onUpdated: (tier: TemplateRangeTier) => void;
}) {
  const toast = useToast();
  const { t } = useTranslation();
  const { request: updateRequest, error } = useApiRequest(
    templateRangeTiersApi.update,
  );

  const { data: tier, refetch: refetchTier } = useApiResource(
    templateRangeTiersApi.get,
  );

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<UpdateTemplateRangeTierRequest>();

  useEffect(() => {
    refetchTier(tierId);
  }, [refetchTier, tierId]);

  useEffect(() => {
    reset({
      name: tier?.name,
      startRange: tier?.startRange,
      endRange: tier?.endRange,
      costMultiplier: tier?.costMultiplier,
    });
  }, [tier, reset]);

  const onSubmit = async (data: UpdateTemplateRangeTierRequest) => {
    await request(
      updateRequest,
      [tier!.id, data],
      (data: TemplateRangeTier) => {
        toast({
          title: t('general.updated'),
          status: 'success',
          isClosable: true,
        });
        onUpdated(data);
        onClose();
      },
    );
  };

  return (
    <>
      {error && <ErrorDetails error={error} />}
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody>
          <Flex direction="column" gap={4}>
            <FormControl isRequired={true} isInvalid={!!errors.name}>
              <TexasFormLabel>{t('general.name')}</TexasFormLabel>
              <Input
                variant="entityDirectory"
                {...register('name', {
                  required: true,
                  maxLength: {
                    value: 100,
                    message: t('errors.maxLength', { count: 100 }),
                  },
                })}
                placeholder={t('general.name')}
              />
              <HookFormCounter control={control} name="name" maxLength={100} />
              <ErrorLabel text={errors.name?.message} />
            </FormControl>
            <FormControl isRequired={true} isInvalid={!!errors.startRange}>
              <TexasFormLabel>{t('configuration.startRange')}</TexasFormLabel>
              <Input
                type="number"
                variant="entityDirectory"
                {...register('startRange', {
                  required: true,
                  valueAsNumber: true,
                })}
                placeholder={t('configuration.startRange')}
              />
              <ErrorLabel text={errors.startRange?.message} />
            </FormControl>
            <FormControl isRequired={true} isInvalid={!!errors.endRange}>
              <TexasFormLabel>{t('configuration.endRange')}</TexasFormLabel>
              <Input
                type="number"
                variant="entityDirectory"
                {...register('endRange', {
                  required: true,
                  valueAsNumber: true,
                })}
                placeholder={t('configuration.endRange')}
              />
              <ErrorLabel text={errors.endRange?.message} />
            </FormControl>
            <FormControl isRequired={true} isInvalid={!!errors.costMultiplier}>
              <TexasFormLabel>
                {t('configuration.costMultiplier')}
              </TexasFormLabel>
              <Input
                type="number"
                step="0.01"
                variant="entityDirectory"
                {...register('costMultiplier', {
                  required: true,
                  valueAsNumber: true,
                })}
                placeholder={t('configuration.costMultiplier')}
              />
              <ErrorLabel text={errors.costMultiplier?.message} />
            </FormControl>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <SubmitButton loading={isSubmitting}>
            {t('general.save')}
          </SubmitButton>
        </ModalFooter>
      </form>
    </>
  );
}
